import { serializePrescriptionForApi } from "../../helpers/vaInvoiceUtils";
import { dmApi } from "../dmApi";
import { getAuthorizationHeader } from "../../helpers/RtkQueryUtils";
import { handleQueryErrorAndSuccess } from "../../helpers/RtkQueryUtils";
import { downloadFile } from "../../helpers/Utils";
const OrderApi = dmApi.injectEndpoints({
  endpoints: (build) => ({
    getodersByFilter: build.mutation({
      query: (params) => {
        // Destructure and set defaults
        const {
          page,
          limit = 20,
          orderSheet,
          ...rest
        } = params;

        const buildQuery = (paramsObj) => {

          return Object.entries(paramsObj)
            .filter(([_, value]) => {
              if (Array.isArray(value)) return value.length > 0;
              if (typeof value === "string") return value.trim() !== "" && value !== "all";
              return value !== undefined && value !== null;
            })
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return value.map((v) => `${key}=${encodeURIComponent(v)}`).join("&");
              }
              return `${key}=${encodeURIComponent(value)}`;
            })
            .join("&");
        };

        let url = `/order?page=${page}&limit=${limit}`;
        const queryString = buildQuery(rest);

        if (queryString) url += `&${queryString}`;

        if (orderSheet) {
          const formData = new FormData();
          formData.append("orderSheet", orderSheet);
          return {
            url,
            method: "POST",
            body: formData,
            headers: {
              ...getAuthorizationHeader(),
            },
          };
        }

        return {
          url,
          method: "POST",
          headers: getAuthorizationHeader(),
        };
      },
      invalidatesTags: ["getOrdersApi"],
    }),

    updateOrderStatus: build.mutation({
      query: (body) => ({
        url: "/order/updateOrdersStatusByShopifyItemIDs",
        method: "PUT",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"],

    }),
    softDeleteOrders: build.mutation({
      query: (body) => ({
        url: "/order/softDelete",
        method: "PUT",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"],
    }),
    approveDistributorOrder: build.mutation({
      query: (body) => ({
        url: "/order/distributor-approval",
        method: "PUT",
        body,
        headers: getAuthorizationHeader(),
      }),
    }),
    updateOrderByShopifyItemID: build.mutation({
      query: (body) => ({
        url: "/order/updateOrderByShopifyItemID",
        method: "PUT",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
    redoOrder: build.mutation({
      query: (body) => ({
        url: "/order/redo",
        method: "POST",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"],
    }),
    undoOrder: build.mutation({
      query: (body) => ({
        url: "/order/undoRedo",
        method: "DELETE",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"],
      
    }),
    uploadOrderSheet: build.mutation({
      query: (body) => ({
        url: "/order/uploadOrderSheet",
        method: "Post",
        body,
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"],
      async onQueryStarted(_, { queryFulfilled }) {
        await handleQueryErrorAndSuccess(queryFulfilled, "", "Order undo");
      },

    }),
    sendPrescriptionMail: build.mutation({
      query: (body) => ({
        url: "/prescriptions/sendPrescriptionMail",
        method: "Post",
        body,
        headers: getAuthorizationHeader(),
      }),
    }),
    sendTrackingLinkMail: build.mutation({
      query: (body) => ({
        url: "/order/sendTrackingLinkMail",
        method: "Post",
        body,
        headers: getAuthorizationHeader(),
      }),
    }),
    downloadOrdersExcel: build.mutation({
      query: (body) => ({
        url: "/order/downloadOrdersExcel",
        method: "Post",
        body,
        headers: {
          ...getAuthorizationHeader(),
          'Content-Disposition': "attachment",
        },
        responseHandler: async (response) => {

          const blob = await response.blob();

          const contentDisposition = response.headers.get('Content-Disposition');

          const filename = contentDisposition

            ? contentDisposition.split('filename=')[1]

            : `orders.xlsx`;

          downloadFile(blob, filename)

          return { success: true };

        },

      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await handleQueryErrorAndSuccess(queryFulfilled, "", "Orders sheet downloaded");
      },

    }),
    downloadAllOrdersExcel: build.mutation({
      query: (body) => ({
        url: "/order/downloadAllOrdersExcel",
        method: "Post",
        body,
        headers: {
          ...getAuthorizationHeader(),
          'Content-Disposition': "attachment",
        },
        responseHandler: async (response) => {

          const blob = await response.blob();

          const contentDisposition = response.headers.get('Content-Disposition');

          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1]
            : `all-orders.xlsx`;

          downloadFile(blob, filename)

          return { success: true };

        },

      }),
    }),
    getTrackingHistory: build.query({
      query: ({ ShopifyID }) => ({
        url: `/order/${ShopifyID}/trackingDetails`,
        method: "POST",
        headers: getAuthorizationHeader(),
      }),
    }),
    markOrderSeen: build.mutation({
      query: ({ body }) => ({
        url: `/order/markOrderSeen`,
        method: "POST",
        headers: getAuthorizationHeader(),
        body: body
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
    markFieldSeen: build.mutation({
      query: ({ body }) => ({
        url: `/order/markFieldSeen`,
        method: "POST",
        headers: getAuthorizationHeader(),
        body: body
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
    uploadTrackingNumberSheet: build.mutation({
      query: ({ formData }) => ({
        url: `/order/uploadTrackingNumberSheet`,
        method: "POST",
        headers: getAuthorizationHeader(),
        body: formData
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
    getProductionOrderStatus: build.mutation({
      query: ({ ItemID }) => ({
        url: `/production/orders/${encodeURIComponent(ItemID)}/status`,
        method: "GET",
        headers: getAuthorizationHeader(),
      }),
    }),
    getAbnormalLensOrders: build.query({
      query: () => ({
        url: "/order/abnormal-lenses",
        method: "GET",
        headers: getAuthorizationHeader(),
      }),
    }),
    sendOrderToProduction: build.mutation({
      query: ({ ItemID, provider, resend = false }) => ({
        url: `/production/orders/${encodeURIComponent(ItemID)}/send`,
        method: "POST",
        headers: getAuthorizationHeader(),
        body: { provider, resend },
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
    uploadOrderPhoto: build.mutation({
      query: ({ ItemID, photo }) => {
        const formData = new FormData();
        formData.append("photo", photo);
        return {
          url: `/order/${encodeURIComponent(ItemID)}/photos`,
          method: "POST",
          headers: getAuthorizationHeader(),
          body: formData,
        };
      },
      invalidatesTags: ["getOrdersApi"]
    }),
    updateOriginalPo: build.mutation({
      query: ({ ItemID, invoicePoNumber, originalPoNumber, originalPoFile }) => {
        const formData = new FormData();
        const poValue =
          invoicePoNumber !== undefined && invoicePoNumber !== null
            ? invoicePoNumber
            : originalPoNumber;
        if (poValue !== undefined && poValue !== null) {
          formData.append("invoicePoNumber", String(poValue));
        }
        if (originalPoFile instanceof File) {
          formData.append("originalPoFile", originalPoFile);
        }
        return {
          url: `/order/${encodeURIComponent(ItemID)}/original-po`,
          method: "POST",
          headers: getAuthorizationHeader(),
          body: formData,
        };
      },
      invalidatesTags: ["getOrdersApi"],
    }),
    updateInvoiceFile: build.mutation({
      query: ({ ItemID, invoiceFile }) => {
        const formData = new FormData();
        if (invoiceFile instanceof File) {
          formData.append("invoiceFile", invoiceFile);
        }
        return {
          url: `/order/${encodeURIComponent(ItemID)}/invoice-file`,
          method: "POST",
          headers: getAuthorizationHeader(),
          body: formData,
        };
      },
      invalidatesTags: ["getOrdersApi"],
    }),
    recognizeVaDevicePhoto: build.mutation({
      query: ({ photo }) => {
        const formData = new FormData();
        formData.append("photo", photo);
        return {
          url: "/order/va-device/recognize",
          method: "POST",
          headers: getAuthorizationHeader(),
          body: formData,
        };
      },
    }),
    createVaDeviceOrder: build.mutation({
      query: ({ photo, macAddress, serialNumber, review }) => {
        const formData = new FormData();
        if (photo instanceof File) formData.append("photo", photo);
        formData.append("macAddress", macAddress);
        formData.append("serialNumber", serialNumber);
        formData.append("review", JSON.stringify({
          ...review,
          prescription: serializePrescriptionForApi(review?.prescription),
          prescriptionFile: undefined,
          originalPoFile: undefined,
        }));
        if (review?.prescriptionFile instanceof File) {
          formData.append("prescriptionFile", review.prescriptionFile);
        }
        return {
          url: "/order/va-device",
          method: "POST",
          headers: getAuthorizationHeader(),
          body: formData,
        };
      },
      invalidatesTags: ["getOrdersApi"],
    }),
      syncAllOrders: build.mutation({
      query: () => ({
        url: `/shopify/syncOrders`,
        method: "POST",
        headers: getAuthorizationHeader(),
      }),
      invalidatesTags: ["getOrdersApi"]
    }),
  }),
});

export const { 
  useGetodersByFilterMutation, 
  useUpdateOrderStatusMutation,
  useSoftDeleteOrdersMutation,
  useApproveDistributorOrderMutation,
  useUpdateOrderByShopifyItemIDMutation, 
  useRedoOrderMutation, 
  useUploadOrderSheetMutation, 
  useUndoOrderMutation, 
  useGetTrackingHistoryQuery, 
  useSendPrescriptionMailMutation, 
  useSendTrackingLinkMailMutation, 
  useDownloadOrdersExcelMutation, 
  useDownloadAllOrdersExcelMutation,
  useMarkOrderSeenMutation, 
  useMarkFieldSeenMutation, 
  useUploadTrackingNumberSheetMutation,
  useUploadOrderPhotoMutation,
  useUpdateOriginalPoMutation,
  useUpdateInvoiceFileMutation,
  useRecognizeVaDevicePhotoMutation,
  useCreateVaDeviceOrderMutation,
  useSendOrderToProductionMutation,
  useGetProductionOrderStatusMutation,
  useGetAbnormalLensOrdersQuery,
  useSyncAllOrdersMutation 

} = OrderApi;
